import React, { useMemo } from 'react';
import { ProductionPiece } from 'app/models/ProductionPiece';
import PickingPageHeader from 'app/containers/OrderDetails/HeaderOrderDetails/PrintOrder/PickingPage/PickingPageHeader';
import { Workstation } from 'app/containers/OrderDetails/types';
import { PartOrder } from 'app/models/PartOrder';
import PickingSectionView from './PickingSectionView';
import { computePickingSubPages } from './computePickingSubPages';
import { CountTextLines } from './types';
import { PickingOrderStrategy, shouldOrderByLocation } from '../pickingOrder';

interface Props {
  order: PartOrder;
  pieces: ProductionPiece[];
  workstation: Workstation;
  trolleyName?: string;
  drawerPieces?: ProductionPiece[];
  countTextLines: CountTextLines;
  linesPerPage?: number;
  pickingOrderStrategy: PickingOrderStrategy;
}

const PickingPageView = ({
  order,
  pieces,
  workstation,
  trolleyName,
  drawerPieces,
  countTextLines,
  linesPerPage,
  pickingOrderStrategy,
}: Props) => {
  const showHardwareSections = workstation === Workstation.Drawer;
  const useLocationOrder = shouldOrderByLocation(
    pickingOrderStrategy,
    workstation,
  );

  const subPages = useMemo(
    () =>
      computePickingSubPages(pieces, drawerPieces, {
        countTextLines,
        linesPerPage,
        workstation,
        useLocationOrder,
      }),
    [
      pieces,
      drawerPieces,
      countTextLines,
      linesPerPage,
      workstation,
      useLocationOrder,
      order.id,
    ],
  );

  return (
    <>
      {subPages.map((subpage, indexPage) => (
        <div key={`${order.id}-${indexPage}`} className="page page-break" lang="fr">
          <PickingPageHeader
            order={order}
            workstation={workstation}
            indexPage={indexPage + 1}
            nbPages={subPages.length}
            trolley={trolleyName}
          />
          <div className="flex flex-col">
            <PickingSectionView
              pieces={subpage.cabinetPieces}
              preserveOrder={useLocationOrder}
            />
            {showHardwareSections && (
              <PickingSectionView
                pieces={subpage.hardwarePieces}
                forceDrawerHardwareSorting
                preserveOrder={useLocationOrder}
                title={
                  subpage.hardwarePieces.length > 0
                    ? 'Visserie / Quincaillerie'
                    : undefined
                }
              />
            )}
            <PickingSectionView
              pieces={subpage.cuttingPieces}
              title={subpage.cuttingPieces.length > 0 ? 'Découpes' : undefined}
            />
            {(subpage.sparePartPieces.length > 0 ||
              subpage.sparePartHardwarePieces.length > 0 ||
              subpage.sparePartCuttingPieces.length > 0) && (
              <div className="flex mt-4 -mb-1">
                <div className="text-xl font-bold">
                  Pièces supplémentaires au meuble du chariot
                </div>
              </div>
            )}
            <PickingSectionView
              pieces={subpage.sparePartPieces}
              preserveOrder={useLocationOrder}
            />
            {showHardwareSections && (
              <PickingSectionView
                pieces={subpage.sparePartHardwarePieces}
                forceDrawerHardwareSorting
                preserveOrder={useLocationOrder}
                title={
                  subpage.sparePartHardwarePieces.length > 0
                    ? 'Visserie / Quincaillerie'
                    : undefined
                }
              />
            )}
            <PickingSectionView
              pieces={subpage.sparePartCuttingPieces}
              title={
                subpage.sparePartCuttingPieces.length > 0 ? 'Découpes' : undefined
              }
            />
          </div>
        </div>
      ))}
    </>
  );
};

export default PickingPageView;
